import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    signIn, 
    signInWithGoogle, 
    resetPassword,
    user,
    profile,
    ready,
    isLoading,
    hasActiveSubscription
  } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  

  const [formLoading, setFormLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const mounted = useRef(true);

  // Get redirect URL from query params
  const requestedRedirect = new URLSearchParams(location.search).get('redirect');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  // Redirect logic - matches AuthRedirect.js flow
  useEffect(() => {
    // Don't redirect until auth is ready
    if (!ready || isLoading) return;

    // Don't redirect if not authenticated
    if (user === false) return;

    // Don't redirect if still loading user data
    if (user === null) return;

    // Wait for profile to be loaded before making redirect decisions
    // This prevents redirecting to onboarding when profile is still null
    if (user && typeof user === 'object' && profile === null) {
      // console.log('Waiting for profile to load before redirect');
      return;
    }

    // Now we know we have an authenticated user with profile data
    if (!profile?.onboarding_completed) {
      navigate('/onboarding', { replace: true });
    } else if (!hasActiveSubscription) {
      // If there's a requested redirect to payment, don't redirect again
      if (requestedRedirect?.includes('/payment')) return;
      navigate('/payment', { replace: true });
    } else {
      navigate(requestedRedirect || '/dashboard', { replace: true });
    }
  }, [user, profile, ready, isLoading, hasActiveSubscription, navigate, requestedRedirect]);

  const validateForm = () => {
    if (!email) {
      setError('Please enter your email address');
      return false;
    }
    if (!password && !isResetMode) {
      setError('Please enter your password');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (isResetMode) {
      return handlePasswordReset();
    }
    
    if (!validateForm()) return;
    
    setFormLoading(true);
    try {
      // Attempt sign in directly
      const result = await signIn(email, password);
      
      const { data, error: signInError } = result;
      
      if (signInError) {
        // Handle specific error cases
        const errorMessage = signInError.message || signInError.toString();
        
        if (errorMessage.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
          return;
        }
        if (errorMessage.includes('Email not confirmed')) {
          setError('Please confirm your email address before signing in. Check your inbox for the confirmation email. If you need help, contact our support team via the chat widget (bottom right) or email support@openorg.fyi');
          return;
        }
        if (errorMessage.includes('Rate limit') || errorMessage.includes('Too many requests')) {
          setError('Too many sign-in attempts. Please wait a few minutes before trying again. If you continue to have issues, contact our support team via the chat widget (bottom right) or email support@openorg.fyi');
          return;
        }
        
        // For any other error, show a user-friendly message
        setError(`${errorMessage || 'Unable to sign in.'} Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.`);
        return;
      }

      if (!data?.user) {
        setError('Unable to sign in. Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.');
        return;
      }

      setSuccess('🎉 Welcome back! Redirecting to your dashboard...');
      // Navigation handled by effect
    } catch (err) {
      // Handle AuthApiError specifically
      if (err.message && err.message.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
        return;
      }
      
      setError(`${err.message || 'An unexpected error occurred.'} Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.`);
    } finally {
      setFormLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address to reset your password');
      return;
    }

    setFormLoading(true);
    try {
      const { success, error } = await resetPassword(email);
      
      if (!success) {
        throw new Error(error?.message || 'Failed to send reset email');
      }
      
      setSuccess('🔗 Password reset email sent! Please check your inbox and spam folder for reset instructions.');
      setError('');
      // Reset form after successful password reset
      setTimeout(() => {
        setIsResetMode(false);
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(`${err.message} If you continue to have issues, contact our support team via the chat widget (bottom right) or email support@openorg.fyi`);
      setSuccess('');
    } finally {
      setFormLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setGoogleLoading(true);
    
    try {
      const { success, error } = await signInWithGoogle();
      if (!mounted.current) return;

      if (!success) {
        throw new Error(error || 'Google sign in failed. Please try again.');
      }

      setSuccess('🎉 Welcome back! Redirecting to your dashboard...');
      // For Google sign in, the redirect is handled by the OAuth flow and AuthRedirect.js
    } catch (err) {
      if (!mounted.current) return;
      setError(`${err.message} If you continue to have issues, contact our support team via the chat widget (bottom right) or email support@openorg.fyi`);
      setGoogleLoading(false);
    }
  };

  const toggleResetMode = () => {
    setIsResetMode(!isResetMode);
    setError('');
    setSuccess('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            {isResetMode ? 'Reset Password' : 'Welcome Back'}
          </h1>
          <p className="text-gray-400">
            {isResetMode 
              ? 'Enter your email to receive reset instructions' 
              : 'Sign in to your account to continue'
            }
          </p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          {/* Success Message */}
          {success && (
            <div className="p-3 mb-4 bg-green-500/20 border border-green-500 rounded">
              <p className="text-green-300 text-center">{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 mb-4 bg-red-500/20 border border-red-500 rounded">
              <p className="text-red-300 text-center">{error}</p>
            </div>
          )}

          {!isResetMode && (
            <>
              {/* Google Sign In Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={formLoading || googleLoading}
                className="w-full mb-4 py-2 px-4 bg-white text-gray-900 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? 'Connecting...' : 'Continue with Google'}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-800 text-gray-400">Or sign in with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                placeholder="Email address"
                required
                autoComplete="email"
              />
            </div>

            {!isResetMode && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={toggleResetMode}
                    className="text-sm text-primary hover:text-primary/90 focus:outline-none focus:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  placeholder="Password"
                  required
                  autoComplete="current-password"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={formLoading || googleLoading}
              className="w-full py-2 px-4 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formLoading 
                ? (isResetMode ? 'Sending Reset Email...' : 'Signing in...') 
                : (isResetMode ? 'Send Reset Email' : 'Sign In with Email')}
            </button>

            {isResetMode && (
              <button
                type="button"
                onClick={toggleResetMode}
                className="w-full py-2 px-4 mt-2 bg-transparent text-gray-400 hover:text-white rounded-md border border-gray-600 hover:border-gray-500 transition-colors"
              >
                Back to Sign In
              </button>
            )}
          </form>
        </div>

        {!isResetMode && (
          <p className="mt-4 text-center text-sm text-gray-400">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/signup')}
              disabled={formLoading || googleLoading}
              className="text-primary hover:text-primary/90 focus:outline-none focus:underline disabled:opacity-50"
            >
              Sign up
            </button>
          </p>
        )}
      </div>
    </div>
  );
} 